import React, { useEffect, useMemo, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    BarChart3,
    CalendarCheck,
    Clock,
    FileSpreadsheet,
    FileText,
    RefreshCw,
    Timer,
    UserX,
    Users,
    HeartPulse,
    ChevronRight,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { getSedesNomina } from '@/pages/personal/nomina/Services/nomina.services';
import { Avatar, Kpi, Pill, RANGOS, duracion } from './controlUi';
import { exportarCentroExcel } from './controlExcel';
import { saveAs } from 'file-saver';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const SERIES = [
    { key: 'onTime', label: 'A tiempo', color: '#10b981' },
    { key: 'late', label: 'Tarde', color: '#ef4444' },
    { key: 'absent', label: 'No se presentó', color: '#7f1d1d' },
    { key: 'leave', label: 'Incapacidad / permiso', color: '#0ea5e9' },
];

const pct = (v) => (v == null ? '—' : `${v} %`);
const tonoPct = (v, alerta) => (v == null ? undefined : v < alerta ? 'text-red-600 dark:text-red-400' : undefined);

const TooltipDia = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="rounded-md border bg-popover p-3 text-sm text-popover-foreground shadow-md">
            <p className="font-semibold capitalize">{format(new Date(`${d.date}T12:00:00`), "EEEE d 'de' MMM", { locale: es })}</p>
            <p className="text-muted-foreground">{d.scheduled} turnos planificados</p>
            <p className="text-emerald-600 dark:text-emerald-400">A tiempo: {d.onTime}</p>
            <p className="text-red-600 dark:text-red-400">
                Tarde: {d.late}
                {d.lateMinutes ? ` · ${duracion(d.lateMinutes)} en total` : ''}
            </p>
            <p className="text-red-900 dark:text-red-300">No se presentaron: {d.absent}</p>
            {d.leave > 0 && <p className="text-sky-600 dark:text-sky-400">Incapacidad / permiso: {d.leave}</p>}
            {d.extra > 0 && <p className="text-violet-600 dark:text-violet-400">Sin turno planificado: {d.extra}</p>}
            <p className="mt-1 text-muted-foreground">
                {d.executedHours} h marcadas · {d.authorizedHours} h autorizadas
            </p>
        </div>
    );
};

const COLUMNAS = [
    { key: 'nombre', label: 'Persona', valor: (f) => `${f.employee.nombre} ${f.employee.apellido}`.toLowerCase() },
    { key: 'turnos', label: 'Turnos', valor: (f) => f.statistics.exigibleDays, right: true, title: 'Turnos cumplidos de los exigibles hasta hoy' },
    { key: 'asistencia', label: 'Asistencia', valor: (f) => f.statistics.attendanceRate ?? -1, right: true },
    { key: 'puntualidad', label: 'Puntualidad', valor: (f) => f.statistics.punctualityRate ?? -1, right: true },
    { key: 'tarde', label: 'Llegadas tarde', valor: (f) => f.statistics.lateDays, right: true },
    { key: 'minutos', label: 'Minutos tarde', valor: (f) => f.statistics.lateMinutes, right: true },
    { key: 'faltas', label: 'No se presentó', valor: (f) => f.statistics.absentDays, right: true },
    { key: 'ausencias', label: 'Incap. / permiso', valor: (f) => f.statistics.incapacityDays + f.statistics.permissionDays, right: true },
    { key: 'ejecutadas', label: 'Ejecutadas', valor: (f) => f.statistics.executedHours, right: true },
    { key: 'autorizadas', label: 'Autorizadas', valor: (f) => f.statistics.authorizedHours, right: true },
];

const AnalisisCentro = ({ onVerPersona }) => {
    const { toast } = useToast();
    const [centros, setCentros] = useState([]);
    const [centerId, setCenterId] = useState('');
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generandoPdf, setGenerandoPdf] = useState(false);

    // PDF del centro: lo arma el backend con el logo y los datos de la empresa.
    const descargarPdf = async () => {
        try {
            setGenerandoPdf(true);
            const response = await axios.get(`${API_URL}/shifts/control/center/pdf`, {
                params: { startDate: resultado.startDate, endDate: resultado.endDate, centerId: resultado.centerId },
                responseType: 'blob',
            });
            const centro = (resultado.centro || 'centro')
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .replace(/[^A-Za-z0-9]+/g, '_');
            saveAs(response.data, `control-centro_${centro}_${resultado.startDate}_a_${resultado.endDate}.pdf`);
        } catch (error) {
            console.error('Error generando el PDF del centro:', error);
            toast({ title: 'Error', description: 'No se pudo generar el reporte PDF del centro.', variant: 'destructive' });
        } finally {
            setGenerandoPdf(false);
        }
    };
    const [orden, setOrden] = useState({ key: 'minutos', dir: 'desc' });

    useEffect(() => {
        getSedesNomina()
            .then(setCentros)
            .catch(() => toast({ title: 'Error', description: 'No se pudieron cargar los centros.', variant: 'destructive' }));
    }, []);

    const generar = async () => {
        if (!centerId) return;
        if (endDate < startDate) {
            toast({ title: 'Rango no válido', description: 'La fecha fin es anterior a la de inicio.', variant: 'destructive' });
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL}/shifts/control/center`, { params: { startDate, endDate, centerId } });
            setResultado({ ...data, centro: centros.find((c) => c.id === centerId)?.nombre || '' });
        } catch (error) {
            console.error('Error en el análisis por centro:', error);
            toast({
                title: 'Error',
                description: error?.response?.data?.message || 'No se pudo cargar el análisis del centro.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const vigente = resultado && resultado.centerId === centerId && resultado.startDate === startDate && resultado.endDate === endDate;
    const t = vigente ? resultado.totals : null;

    const serie = useMemo(
        () =>
            (vigente ? resultado.days : [])
                .filter((d) => d.scheduled || d.extra)
                .map((d) => ({
                    ...d,
                    etiqueta: format(new Date(`${d.date}T12:00:00`), 'dd/MM'),
                    onTime: Math.max(0, d.attended - d.late),
                })),
        [resultado, vigente],
    );

    const personas = useMemo(() => {
        if (!vigente) return [];
        const col = COLUMNAS.find((c) => c.key === orden.key) || COLUMNAS[0];
        const dir = orden.dir === 'asc' ? 1 : -1;
        return [...resultado.employees].sort((a, b) => {
            const va = col.valor(a);
            const vb = col.valor(b);
            return va < vb ? -dir : va > vb ? dir : 0;
        });
    }, [resultado, vigente, orden]);

    const ordenar = (key) =>
        setOrden((o) => ({ key, dir: o.key === key && o.dir === 'desc' ? 'asc' : key === 'nombre' ? 'asc' : 'desc' }));

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_repeat(2,minmax(0,1fr))_auto] md:items-end">
                        <div className="space-y-1">
                            <Label className="text-xs">Centro</Label>
                            <Select value={centerId} onValueChange={setCenterId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecciona un centro" />
                                </SelectTrigger>
                                <SelectContent>
                                    {centros.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Desde</Label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Hasta</Label>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                        <Button onClick={generar} disabled={loading || !centerId}>
                            {loading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <BarChart3 className="mr-2 h-4 w-4" />}
                            {loading ? 'Generando…' : 'Generar reporte'}
                        </Button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {RANGOS.map((r) => (
                            <Button
                                key={r.label}
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => {
                                    const [a, b] = r.calc();
                                    setStartDate(format(a, 'yyyy-MM-dd'));
                                    setEndDate(format(b, 'yyyy-MM-dd'));
                                }}
                            >
                                {r.label}
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {!vigente ? (
                <Card>
                    <CardContent className="flex h-56 flex-col items-center justify-center text-center">
                        <BarChart3 className="mb-3 h-10 w-10 text-muted-foreground/60" />
                        <p className="text-muted-foreground">Selecciona un centro y el rango, y genera el reporte de asistencia y puntualidad del equipo.</p>
                    </CardContent>
                </Card>
            ) : !resultado.employees.length ? (
                <Card>
                    <CardContent className="flex h-40 items-center justify-center text-muted-foreground">
                        El centro no tiene turnos ni marcaciones en ese rango.
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <h3 className="text-lg font-semibold">{resultado.centro}</h3>
                            <p className="text-xs text-muted-foreground">
                                Del {format(new Date(`${resultado.startDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })} al{' '}
                                {format(new Date(`${resultado.endDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })}
                                {resultado.endDate > resultado.today && ' · los días futuros solo cuentan como planificados'}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    exportarCentroExcel(resultado).catch((e) =>
                                        toast({ title: 'No se exportó', description: e.message, variant: 'destructive' }),
                                    )
                                }
                            >
                                <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
                            </Button>
                            <Button size="sm" onClick={descargarPdf} disabled={generandoPdf}>
                                {generandoPdf ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                                {generandoPdf ? 'Generando…' : 'Reporte PDF'}
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <Kpi icon={Users} label="Personas" value={t.people} sub={`${t.peopleWithLate} llegaron tarde al menos una vez`} />
                        <Kpi
                            icon={CalendarCheck}
                            label="Asistencia"
                            value={pct(t.attendanceRate)}
                            sub={`${t.plannedWorkedDays} de ${t.exigibleDays} turnos cumplidos`}
                            tone={tonoPct(t.attendanceRate, 90)}
                        />
                        <Kpi
                            icon={Clock}
                            label="Puntualidad"
                            value={pct(t.punctualityRate)}
                            sub={`${t.onTimeDays} a tiempo · ${t.lateDays} tarde`}
                            tone={t.punctualityRate != null && t.punctualityRate < 85 ? 'text-amber-600 dark:text-amber-400' : undefined}
                        />
                        <Kpi
                            icon={Timer}
                            label="Minutos tarde"
                            value={duracion(t.lateMinutes)}
                            sub={t.lateDays ? `Promedio ${Math.round(t.lateMinutes / t.lateDays)} min por llegada tarde` : 'Sin llegadas tarde'}
                            tone={t.lateMinutes ? 'text-red-600 dark:text-red-400' : undefined}
                        />
                        <Kpi
                            icon={UserX}
                            label="No se presentó"
                            value={t.absentDays}
                            sub={`${t.incapacityDays} días de incapacidad · ${t.permissionDays} de permiso`}
                            tone={t.absentDays ? 'text-red-600 dark:text-red-400' : undefined}
                        />
                        <Kpi
                            icon={HeartPulse}
                            label="Horas ejecutadas"
                            value={`${t.executedHours} h`}
                            sub={`${t.authorizedHours} h autorizadas${t.pendingAuthorizationDays ? ` · ${t.pendingAuthorizationDays} días sin autorizar` : ''}`}
                        />
                    </div>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Asistencia por día</CardTitle>
                            <CardDescription>Turnos planificados de cada día según cómo llegó el personal. Pasa el mouse para ver los minutos tarde y las horas.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="mb-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                                {SERIES.map((s) => (
                                    <span key={s.key} className="inline-flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} /> {s.label}
                                    </span>
                                ))}
                            </div>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                                    <XAxis dataKey="etiqueta" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                    <YAxis fontSize={11} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
                                    <Tooltip content={<TooltipDia />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
                                    {SERIES.map((s, i) => (
                                        <Bar
                                            key={s.key}
                                            dataKey={s.key}
                                            stackId="dia"
                                            fill={s.color}
                                            maxBarSize={30}
                                            radius={i === SERIES.length - 1 ? [3, 3, 0, 0] : 0}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Por persona</CardTitle>
                            <CardDescription>
                                Turnos y marcaciones en este centro. Haz clic en una persona para ver su análisis día a día.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto rounded-md border">
                                <Table className="[&_td]:py-2">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            {COLUMNAS.map((c) => (
                                                <TableHead
                                                    key={c.key}
                                                    title={c.title}
                                                    className={cn('cursor-pointer select-none whitespace-nowrap hover:text-foreground', c.right && 'text-right')}
                                                    onClick={() => ordenar(c.key)}
                                                >
                                                    {c.label}
                                                    {orden.key !== c.key ? (
                                                        <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 opacity-50" />
                                                    ) : orden.dir === 'asc' ? (
                                                        <ArrowUp className="ml-1 inline h-3.5 w-3.5" />
                                                    ) : (
                                                        <ArrowDown className="ml-1 inline h-3.5 w-3.5" />
                                                    )}
                                                </TableHead>
                                            ))}
                                            <TableHead aria-label="Ver detalle" />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {personas.map((f) => {
                                            const st = f.statistics;
                                            return (
                                                <TableRow
                                                    key={f.employee.id}
                                                    className="cursor-pointer"
                                                    onClick={() => onVerPersona(f.employee.id, resultado.startDate, resultado.endDate)}
                                                >
                                                    <TableCell className="min-w-[260px]">
                                                        <div className="flex items-center gap-3">
                                                            <Avatar emp={f.employee} size="h-8 w-8" />
                                                            <div className="min-w-0">
                                                                <div className="font-medium leading-tight">
                                                                    {f.employee.nombre} {f.employee.apellido}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {f.employee.cedula || 'Sin cédula'}
                                                                    {f.employee.lavanderia?.nombre && f.employee.lavanderia.nombre !== resultado.centro && ` · hoy en ${f.employee.lavanderia.nombre}`}
                                                                    {f.employee.estado && f.employee.estado !== 'Activo' && ` · ${f.employee.estado}`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">
                                                        {st.plannedWorkedDays}/{st.exigibleDays}
                                                        {st.extraDays > 0 && <div className="whitespace-nowrap text-xs text-muted-foreground">+{st.extraDays} sin turno</div>}
                                                    </TableCell>
                                                    <TableCell className={cn('text-right font-medium tabular-nums', tonoPct(st.attendanceRate, 90))}>
                                                        {pct(st.attendanceRate)}
                                                    </TableCell>
                                                    <TableCell
                                                        className={cn(
                                                            'text-right font-medium tabular-nums',
                                                            st.punctualityRate != null && st.punctualityRate < 85 && 'text-amber-600 dark:text-amber-400',
                                                        )}
                                                    >
                                                        {pct(st.punctualityRate)}
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">{st.lateDays || '—'}</TableCell>
                                                    <TableCell className={cn('text-right tabular-nums', st.lateMinutes && 'font-medium text-red-600 dark:text-red-400')}>
                                                        {st.lateMinutes ? duracion(st.lateMinutes) : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {st.absentDays ? <Pill tono="rojo">{st.absentDays}</Pill> : <span className="text-muted-foreground">—</span>}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {st.incapacityDays + st.permissionDays ? (
                                                            <Pill tono="azul" title={`${st.incapacityDays} de incapacidad · ${st.permissionDays} de permiso`}>
                                                                {st.incapacityDays + st.permissionDays}
                                                            </Pill>
                                                        ) : (
                                                            <span className="text-muted-foreground">—</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right tabular-nums">{st.executedHours} h</TableCell>
                                                    <TableCell className="text-right tabular-nums">
                                                        {st.authorizedHours} h
                                                        {st.pendingAuthorizationDays > 0 && (
                                                            <div className="text-xs text-amber-600 dark:text-amber-400">{st.pendingAuthorizationDays} días sin autorizar</div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                            <p className="mt-3 text-xs text-muted-foreground">
                                Incluye a quien tiene la ficha activa en el centro y a quien tuvo turnos o marcaciones en él en el rango. Solo se
                                miden los turnos y marcaciones de este centro; las incapacidades y permisos registrados no cuentan como
                                inasistencia. La asistencia mide los turnos hasta hoy.
                            </p>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default AnalisisCentro;
